/**
 * The Auctogon logo — web skin (BL-1127, ADR 030 § 1).
 *
 * A thin renderer over the shared geometry in `@auctogon/ui/brand`: it picks a
 * variant, sizes the box, and gets out of the way. All structure — the mark's
 * shapes, the traced Syne ExtraBold wordmark, and the alignment between them —
 * lives in the geometry module, so this component and its react-native-svg twin
 * in `apps/admin-catalog` are structurally identical and cannot drift.
 *
 * WHY the wordmark is artwork and not text: outlined paths render identically on
 * web, React Native, favicons and email, with no webfont to load, substitute or
 * miss — which is what lets admin-web honour the brand's ExtraBold spec while
 * loading no Syne font at all (ADR 030 § 1b).
 *
 * WHY `fill="currentColor"` and no colour prop: every surface binds its own
 * dialect token — `text-secondary` (mint) on the dark teal rail, `text-primary`
 * on the white auth pages. A colour baked in here would force each call site to
 * override it and would defeat `brandhex/no-hardcoded-brand-hex`.
 */
'use client';

import type { CSSProperties } from 'react';

import { AUCTOGON_LOCKUP, AUCTOGON_MARK, AUCTOGON_WORDMARK } from '../isomorphic/brand';
import type { BrandArtwork } from '../isomorphic/brand';

/** Which piece of the brand artwork to draw. */
export type AuctogonLogoVariant = 'lockup' | 'mark' | 'wordmark';

const ARTWORK: Record<AuctogonLogoVariant, BrandArtwork> = {
    lockup: AUCTOGON_LOCKUP,
    mark: AUCTOGON_MARK,
    wordmark: AUCTOGON_WORDMARK,
};

export interface AuctogonLogoProps {
    /**
     * Which artwork to draw. Defaults to `lockup` (mark + wordmark) — the full
     * logo is the right answer wherever there's room for it.
     */
    variant?: AuctogonLogoVariant;
    /**
     * Rendered height in px. Width is derived from the variant's aspect ratio so
     * the artwork can never stretch. Omit to size with CSS instead.
     */
    height?: number;
    /** Classes for colour and layout — this is how consumers bind a dialect token. */
    className?: string;
    /**
     * Escape hatch for a computed offset a class can't express — specifically a
     * consumer that composes `mark` + `wordmark` as separate elements and has to
     * restore the shared-baseline alignment the `lockup` variant bakes in (the
     * admin side rail does this around its bordered mark-box). Prefer `className`
     * for anything a Tailwind token covers.
     */
    style?: CSSProperties;
    /**
     * Accessible name. Pass it ONLY where the logo is the sole thing naming its
     * region (the collapsed rail, the auth-page headers). Omitted by default so
     * the logo stays decorative next to visible text rather than announcing
     * "Auctogon" twice.
     */
    title?: string;
}

/**
 * Render the Auctogon logo.
 *
 * @param props - Variant, optional pixel height, classes, style, accessible name.
 * @returns An inline `<svg>` inheriting colour from its nearest text colour.
 */
export function AuctogonLogo({
    variant = 'lockup',
    height,
    className,
    style,
    title,
}: AuctogonLogoProps) {
    const artwork = ARTWORK[variant];
    // Deriving width from height (rather than setting both, or neither) keeps the
    // artwork's proportions authoritative while still letting a caller size by the
    // one dimension layouts actually constrain.
    const dimensions =
        height == null ? {} : { height, width: Number((height * artwork.aspectRatio).toFixed(2)) };

    return (
        <svg
            viewBox={artwork.viewBox}
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={style}
            {...dimensions}
            {...(title ? { role: 'img' } : { 'aria-hidden': true })}
        >
            {title ? <title>{title}</title> : null}
            {artwork.paths.map((d) => (
                <path key={d} d={d} />
            ))}
        </svg>
    );
}
